/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { Injectable, Renderer2, RendererFactory2, TemplateRef, ViewContainerRef } from '@angular/core';
import { ConnectionPositionPair, Overlay, OverlayConfig, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { TemplatePortal } from '@angular/cdk/portal';
import { filter, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ContextMenuService {
  private overlayRef: OverlayRef;
  private renderer: Renderer2;
  private contextMenuSubscription: Subscription;
  private afterClosed = new Subject<ContextMenuData>();
  private timer = null;
  private _data = null;

  onClosed = this.afterClosed.asObservable();

  constructor(private overlay: Overlay, private rendererFactory: RendererFactory2) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  openContextMenu(
    boundary: DOMRect,
    e: MouseEvent,
    menu: TemplateRef<any>,
    viewContainerRef: ViewContainerRef,
    data?: any
  ) {
    this.close(null);
    this._data = data;
    const config = this.getOverlayConfig({
      origin: {
        x: e.clientX,
        y: e.clientY
      }
    });
    this.overlayRef = this.overlay.create(config);
    this.overlayRef.attach(
      new TemplatePortal(menu, viewContainerRef, {
        $implicit: data,
        close: this.close
      })
    );

    // fixing issue for web component.
    this.overlayRef.overlayElement.style.top = `${e.clientY}.px`;
    this.overlayRef.overlayElement.style.left = `${e.clientX}.px`;

    this.timer = setTimeout(() => {
      const overlayRect =
        this.overlayRef.overlayElement.children.item(0) &&
        this.overlayRef.overlayElement.children.item(0).getBoundingClientRect();
      if (!!overlayRect) {
        let apply = false;
        const origin = { x: e.clientX, y: e.clientY };
        if (overlayRect.width + overlayRect.x > boundary.right) {
          apply = true;
          origin.x = boundary.right - overlayRect.width - 5;
        }

        if (boundary.height > overlayRect.height && overlayRect.height + overlayRect.y > boundary.bottom) {
          apply = true;
          origin.y = boundary.height - overlayRect.height - 5;
        }

        if (origin.y < boundary.y || overlayRect.height > boundary.height) {
          this.renderer.setStyle(this.overlayRef.overlayElement.children.item(0), 'max-height', `${boundary.height}px`);
          this.renderer.setStyle(this.overlayRef.overlayElement.children.item(0), 'overflow-y', 'auto');
          origin.y = boundary.y;
          apply = true;
        }

        if (apply) {
          this.overlayRef.updatePositionStrategy(this.getOverlayPosition(origin));
        }
      }

      clearTimeout(this.timer);
    });

    this.contextMenuSubscription = fromEvent<MouseEvent>(document, 'click')
      .pipe(
        filter(event => {
          const clickTarget = event.target as HTMLElement;
          return clickTarget != e.target && !!this.overlayRef && !this.overlayRef.overlayElement.contains(clickTarget);
        }),
        take(1)
      )
      .subscribe(() => {
        this.close(null);
      });

    return this.onClosed.pipe(take(1));
  }

  close = (data?: any) => {
    this.contextMenuSubscription && this.contextMenuSubscription.unsubscribe();

    this.timer !== null && clearTimeout(this.timer);
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
      this.afterClosed.next({
        menuOperation: data,
        data: this._data
      });
      this._data = null;
    }
  };

  private getOverlayPosition(origin: any): PositionStrategy {
    return this.overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions(this.getPositions())
      .withFlexibleDimensions(false)
      .withPush(true);
  }

  private getOverlayConfig({ origin }): OverlayConfig {
    return new OverlayConfig({
      hasBackdrop: false,
      positionStrategy: this.getOverlayPosition(origin),
      scrollStrategy: this.overlay.scrollStrategies.close()
    });
  }

  private getPositions(): ConnectionPositionPair[] {
    return [
      {
        originX: 'end',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'top'
      },
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'top'
      },
      {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'bottom'
      },
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'bottom'
      }
    ];
  }
}

export interface ContextMenuData {
  menuOperation?: any;
  data?: any;
}
